<template>
  <div class="analyses-data-index">
    <Filters
      :filters="insertAnalysesDataFilters"
      filter-key="insertAnalysesData"
      :initial-data="filtersStatus.insertAnalysesData"
      :loading="filtersLoading"
    />

    <InsertExpectedValues
      v-if="expectedValuesModal && selectedSamples.length > 0"
      :samples="selectedSamples"
      @dialogClose="handleDialogClose"
    />

    <UpdateStatus
      v-if="openUpdateStatusModal && selectedSamples.length > 0"
      :samplesId="selectedSamplesId"
      :possibleActions="BULK_ACTION_MODAL"
      auto-submit
      @dialogClose="handleUpdateStatusClose"
    />

    <ProgressDialog
      v-if="displayRawDataProgress"
      :function="updateRawDataFunction"
      :arguments="updateRawDataArguments"
      @dialogClose="rawDataprogressCompleted"
    />

    <InsertValuesTemperatureBulk
      v-if="openInsertValuesTemperatureModal"
      @dialogClose="handleInsertValuesTemperatureClose"
    />

    <InsertSymLotBulk
      v-if="openInsertSymLotModal"
      @dialogClose="handleInsertSymLotClose"
    />

    <ImportAnalysisValues
      v-if="openImportValuesModal"
      @dialogClose="handleImportValuesClose"
    />

    <ViewActionButtons :buttons="actionButtons" />

    <ViewActionButtons
      :disabled="selectedSamples.length === 0"
      :buttons="actionButtonsOnSelection"
    />

    <ChangeDilution
      v-if="openChangeDilutionModal && selectedSamples.length > 0"
      :samples="selectedSamples"
      @dialogClose="handleChangeDilutionDialogClose"
    />

    <ChangeSamplingInformation
      v-if="openChangeSamplingInformation && selectedSamples.length > 0"
      :samples="selectedSamples"
      @dialogClose="handleChangeSamplingInformationDialogClose"
    />

    <ShowComment
      v-if="openShowCommentModal"
      :sample="sample"
      :visible.sync="openShowCommentModal"
      @dialogClose="handleCloseShowCommentForSampleModal"
    />

    <TableWrapper
      :table-columns="insertAnalysesDataColumns"
      table-key="insertAnalysesData"
      :enableSelection="true"
      :massive-actions-options="bulkActions"
      configurableColumns
      :custom-row-class="getRowStatusClass"
      @insertExpectedValues="openExpectedValuesModalSingle"
      @viewAnalysesDetails="gotoSampleDetails"
      @askApproval="askApprovalForSample"
      @showComment="showCommentForSample"
      @changeDilution="changeDilutionForSample"
      @massiveAction="bulkApply"
      @selectionChange="handleMultiSelectionChange"
      @editSamplingInformation="changeSamplingInformation"
      @downloadAttachment="downloadAttachment"
    />

    <SampleDetailsModal
      v-if="openSampleDetailsModal && detailsSampleIds.length > 0"
      :sampleIds="detailsSampleIds"
      tabbable
      :tabKey="
        detailsSampleIds.length > 1 ? 'adcombined' : `${detailsSampleIds[0]}`
      "
      :tabLabel="$t('analysesData.combinedTab')"
      @dialogClose="handleCloseSampleDetailsModal"
    />
  </div>
</template>

<script>
  import { mapGetters, mapActions } from 'vuex';
  import fileSaver from 'file-saver';

  import TableWrapper from '@/components/TableWrapper.vue';
  import Filters from '@/components/Filters.vue';
  import ViewActionButtons from '@/components/ViewActionButtons.vue';
  import SampleDetailsModal from '@/views/sampleDetails/SampleDetailsModal.vue';
  import ProgressDialog from '@/components/ProgressDialog.vue';
  import {
    insertAnalysesDataColumns,
    insertAnalysesDataFilters,
  } from '@/config';
  import {
    SAMPLE_ACTIONS,
    LAB_SAMPLE_STATUSES,
    insertAnalysesDataBulkActions,
  } from '@/config/constants';
  import { analysesDataApi, samplesApi } from '@/api';
  import filterLoadingMixin from '@/mixins/filterLoadingMixin';
  import abortControllerMixin from '@/mixins/abortControllerMixin';
  import axios from 'axios';

  import UpdateStatus from '../../components/UpdateStatus.vue';
  import InsertExpectedValues from './InsertExpectedValues.vue';
  import InsertValuesTemperatureBulk from './InsertValuesTemperatureBulk.vue';
  import InsertSymLotBulk from './InsertSymLotBulk.vue';
  import ChangeDilution from './ChangeDilution.vue';
  import ChangeSamplingInformation from './ChangeSamplingInformation.vue';
  import ShowComment from './ShowComment.vue';
  import ImportAnalysisValues from './importValues/ImportAnalysisValues.vue';

  export default {
    name: 'InsertAnalysesDataIndex',
    mixins: [filterLoadingMixin, abortControllerMixin],
    components: {
      TableWrapper,
      Filters,
      UpdateStatus,
      InsertExpectedValues,
      ViewActionButtons,
      SampleDetailsModal,
      ProgressDialog,
      InsertValuesTemperatureBulk,
      InsertSymLotBulk,
      ChangeDilution,
      ChangeSamplingInformation,
      ShowComment,
      ImportAnalysisValues,
    },
    data() {
      return {
        sample: null,
        insertAnalysesDataColumns,
        insertAnalysesDataFilters,
        bulkActions: Object.entries(insertAnalysesDataBulkActions).map(
          ([key, value]) => ({
            label: this.$t(`analysesData.bulkActions.${key}`),
            value,
          })
        ),
        openUpdateStatusModal: false,
        openShowCommentModal: false,
        expectedValuesModal: false,
        openSampleDetailsModal: false,
        openChangeDilutionModal: false,
        openInsertValuesTemperatureModal: false,
        openInsertSymLotModal: false,
        openImportValuesModal: false,
        selectedSamples: [],
        detailsSampleIds: [],
        BULK_ACTION_MODAL: {
          ASKAPPROVAL: SAMPLE_ACTIONS.ASKAPPROVAL,
        },
        initialStatusFilters: [
          {
            key: LAB_SAMPLE_STATUSES.ACCEPTED,
            label: this.$t('enums.sampleStatuses.accepted'),
          },
          {
            key: LAB_SAMPLE_STATUSES.INPROGRESS,
            label: this.$t('enums.sampleStatuses.inprogress'),
          },
          {
            key: LAB_SAMPLE_STATUSES.TOBEVERIFIED,
            label: this.$t('enums.sampleStatuses.toBeVerified'),
          },
        ],
        actionButtonsOnSelection: [
          {
            text: this.$t('analysesData.bulkActions.viewCombined'),
            type: 'secondary',
            permissions: ['analysesDataView'],
            action: this.handleOpenSampleDetailsModal,
          },
          {
            text: this.$t(
              'analysesData.expectedValues.insertExpectedValuesBulk'
            ),
            type: 'primary',
            permissions: ['analysesDataManage'],
            action: this.openExpectedValuesModalBulk,
          },
        ],
        actionButtons: [
          {
            text: this.$t('analysesData.bulkActions.insertTemperature'),
            type: 'primary',
            permissions: ['analysesDataManage'],
            action: this.handleOpenInsertValuesTemperatureModal,
          },
          {
            text: this.$t('analysesData.bulkActions.insertSymLot'),
            type: 'primary',
            permissions: ['analysesDataManage'],
            action: this.handleOpenInsertSymLotModal,
          },
          {
            text: this.$t('analysesData.bulkActions.importFromFile'),
            type: 'primary',
            icon: 'el-icon-upload2',
            permissions: ['analysesDataManage'],
            action: this.handleOpenImportValuesModal,
          },
        ],
        displayRawData: undefined,
        displayRawDataProgress: false,
        openChangeSamplingInformation: false,
      };
    },
    computed: {
      ...mapGetters(['tableFilters', 'filtersStatus', 'tablesStatus']),
      selectedSamplesId() {
        return this.selectedSamples.map((sample) => sample.id);
      },
      updateRawDataFunction() {
        return samplesApi.bulkUpdateRawData;
      },
      updateRawDataArguments() {
        return [
          [this.selectedSamplesId, { displayRawData: this.displayRawData }],
        ];
      },
    },
    methods: {
      ...mapActions([
        'updateTableRows',
        'updateTableCount',
        'parseQueryStringStatus',
        'addTab',
      ]),
      async tableRows() {
        const signal = this.createAbortController();
        this.filtersLoading = true;
        let aborted = false;
        try {
          let status =
            this.filtersStatus.insertAnalysesData.status ||
            this.initialStatusFilters;

          if (status.length === 0) {
            // restore default statuses
            status = this.initialStatusFilters;
          }

          const samplesIndexResponse = await analysesDataApi.samplesIndex(
            this.tableFilters('insertAnalysesData'),
            {
              ...this.filtersStatus.insertAnalysesData,
              status,
            },
            { signal }
          );
          const samplesIndexCountResponse = await analysesDataApi.samplesCount(
            {
              ...this.filtersStatus.insertAnalysesData,
              status,
            },
            { signal }
          );
          this.updateTableRows({
            tableKey: 'insertAnalysesData',
            rows: samplesIndexResponse.data,
          });
          this.updateTableCount({
            tableKey: 'insertAnalysesData',
            count: samplesIndexCountResponse.data.count,
          });
        } catch (error) {
          if (axios.isCancel(error)) {
            aborted = true;
            return;
          }
          this.$notify({
            showClose: true,
            type: 'error',
            message: error.response.data.error.message,
          });
        } finally {
          if (!aborted) {
            this.filtersLoading = false;
          }
        }
      },
      async bulkApply(action, selectedRows) {
        this.selectedSamples = selectedRows;
        switch (action) {
          case insertAnalysesDataBulkActions.enableRawData:
            await this.changeRawData(true);
            break;
          case insertAnalysesDataBulkActions.disableRawData:
            await this.changeRawData(false);
            break;
          case insertAnalysesDataBulkActions.insertExpectedValues:
            this.openExpectedValuesModalBulk();
            break;
          case insertAnalysesDataBulkActions.bulkAskApproval:
            this.openUpdateStatusModal = true;
            break;
          default:
            break;
        }
      },
      handleUpdateStatusClose(status) {
        this.openUpdateStatusModal = false;
        this.selectedSamples = [];
        if (status.success) this.tableRows();
      },
      changeRawData(displayRawData) {
        this.displayRawData = displayRawData;
        this.displayRawDataProgress = true;
      },
      rawDataprogressCompleted() {
        this.displayRawData = undefined;
        this.displayRawDataProgress = false;
        this.tableRows();
      },
      openExpectedValuesModalSingle(row) {
        this.selectedSamples = [row];
        this.expectedValuesModal = true;
      },
      openExpectedValuesModalBulk() {
        this.expectedValuesModal = true;
      },
      handleDialogClose(status) {
        this.selectedSamples = [];
        this.expectedValuesModal = false;
        if (status.success) this.tableRows();
      },
      gotoSampleDetails(row) {
        this.detailsSampleIds = [row.id];
        this.openSampleDetailsModal = true;
      },
      handleOpenSampleDetailsModal() {
        this.detailsSampleIds = this.selectedSamples.map((s) => s.id);
        this.openSampleDetailsModal = true;
      },
      handleCloseSampleDetailsModal() {
        this.detailsSampleIds = [];
        this.openSampleDetailsModal = false;
        this.tableRows();
      },
      askApprovalForSample(row) {
        this.selectedSamples = [row];
        this.openUpdateStatusModal = true;
      },
      showCommentForSample(row) {
        this.openShowCommentModal = true;
        this.sample = row;
      },
      handleCloseShowCommentForSampleModal() {
        this.sample = null;
        this.openShowCommentModal = false;
      },
      changeDilutionForSample(row) {
        this.selectedSamples = [row];
        this.openChangeDilutionModal = true;
      },
      handleChangeDilutionDialogClose(status) {
        this.selectedSamples = [];
        this.openChangeDilutionModal = false;
        if (status.success) this.tableRows();
      },
      getRowStatusClass({ row }) {
        if (row.status && row.status === LAB_SAMPLE_STATUSES.REJECTED) {
          return 'sample-row-rejected';
        }
        if (row.stabilityTimepointToken) {
          return 'sample-row-stability';
        }
        return '';
      },
      handleMultiSelectionChange(rows) {
        this.selectedSamples = rows;
      },
      handleOpenInsertValuesTemperatureModal() {
        this.openInsertValuesTemperatureModal = true;
      },
      handleInsertValuesTemperatureClose(status) {
        this.openInsertValuesTemperatureModal = false;
        if (status.success) this.tableRows();
      },
      handleOpenInsertSymLotModal() {
        this.openInsertSymLotModal = true;
      },
      handleInsertSymLotClose(status) {
        this.openInsertSymLotModal = false;
        if (status.success) this.tableRows();
      },
      handleOpenImportValuesModal() {
        this.openImportValuesModal = true;
      },
      handleImportValuesClose(status) {
        this.openImportValuesModal = false;
        if (status.success) this.tableRows();
      },
      changeSamplingInformation(row) {
        this.selectedSamples = [row];
        this.openChangeSamplingInformation = true;
      },
      handleChangeSamplingInformationDialogClose(status) {
        this.selectedSamples = [];
        this.openChangeSamplingInformation = false;
        if (status.success) this.tableRows();
      },
      async downloadAttachment(row) {
        try {
          const blob = await samplesApi.downloadAttachmentsZip(row.id);
          fileSaver.saveAs(blob.data, `sample-${row.id}-attachment.zip`);
          return false;
        } catch (error) {
          this.$notify({
            showClose: true,
            type: 'error',
            message: error.response.data.error.message,
          });
          return false;
        }
      },
    },
    created() {
      const query = { ...this.$route.query };
      this.parseQueryStringStatus(query);
      this.tableRows();
    },
    beforeRouteUpdate(to, from, next) {
      this.parseQueryStringStatus(to.query);
      this.tableRows();
      next();
    },
  };
</script>

<style lang="scss">
  .analyses-data-index {
    .table-cell-status {
      position: relative;

      @each $modifier, $map in $sample-statuses {
        &--#{$modifier} {
          & .cell {
            padding-left: 20px;
          }

          &:after {
            content: '';
            display: block;
            width: 10px;
            position: absolute;
            height: 100%;
            top: 0;
            left: 0;
            background: map-get($map, 'background');
          }
        }
      }
    }
    .el-table {
      &__row {
        &.sample-row-rejected {
          background: map-get(
            map-get($sample-statuses, 'rejected'),
            'background'
          );
        }
        &.sample-row-stability {
          background: #1b3a4b;
        }
      }
    }
  }
</style>
